import React, { useState, forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DropDown.module.scss';
import bimloaFilterStyles from '../BimloaFilter/BimloaFilter.module.scss';
import Icon from '../Icon/Icon';
import classes from 'classnames';
import DatePicker from '../BimloaFilter/DatePicker';

export interface Option {
  label: string;
  value: string;
  data?: any;
}

interface DropDownProps {
  options: Option[];
  Open?: boolean;
  placeholder?: string;
  value?: Option;
  onChange?: (value: Option) => void;
  onBlur?: () => void;
  staticTitle?: string;
  style?: React.CSSProperties;
  dropdownContainerClassName?: string;
  dropdownContainerOpenClassName?: string;
  inputContainerClassName?: string;
}

const DropDown = forwardRef<HTMLDivElement, DropDownProps>(
  (
    {
      options,
      Open,
      placeholder,
      value,
      onChange,
      onBlur,
      staticTitle,
      style,
      dropdownContainerClassName,
      dropdownContainerOpenClassName,
      inputContainerClassName,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(Open || false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (isOpen) {
        const handleOutsideClick = (event: any) => {
          if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
          document.removeEventListener('mousedown', handleOutsideClick);
          document.removeEventListener('touchstart', handleOutsideClick);
        };
      }
    }, [isOpen]);

    const toggleDropdown = () => setIsOpen(!isOpen);

    const handleOptionClick = (option: Option) => {
      if (onChange) onChange(option);
      if (option.data?.leaveOpen) return;
      setIsOpen(false);
    };

    const finalPlaceholder = placeholder || t('genericTable.dropDown.placeholder');

    const dropdownHeader = (
      <div className={styles.dropdownHeader} onClick={toggleDropdown}>
        <span className={styles.label}>
          <span>{staticTitle}</span>
          <span>{value ? value.label : finalPlaceholder}</span>
        </span>
        <Icon icon='dropDown' className={styles.dropdownArrow} size={16} />
      </div>
    );

    return (
      <div
        className={classes(styles.inputContainer, {
          [inputContainerClassName || '']: inputContainerClassName,
        })}
        style={style}
        ref={containerRef}
      >
        <div style={{ visibility: 'hidden' }}>{dropdownHeader}</div>
        <div
          className={classes(styles.dropdownContainer, {
            [styles.open]: isOpen,
            [dropdownContainerClassName || '']: dropdownContainerClassName,
            [dropdownContainerOpenClassName || '']: dropdownContainerClassName && isOpen,
          })}
          tabIndex={-1}
          ref={ref}
          onBlur={onBlur}
        >
          {dropdownHeader}
          <ul className={styles.dropdownList}>
            {options.map((option, index) => (
              <React.Fragment key={index}>
                <li
                  className={classes(styles.dropdownItem, {
                    [styles.selected]: value?.value === option.value,
                  })}
                  onClick={() => handleOptionClick(option)}
                >
                  <span className={styles.optionText}>{option.label}</span>
                  {value?.value === option.value && (
                    <span className={styles.checkmark}>
                      <Icon icon='dropDownV' size={24} />
                    </span>
                  )}
                </li>
                {value?.value === option.value &&
                  value?.data?.leaveOpen &&
                  value?.data?.type === 'date' && (
                    <li className={classes(styles.dropdownItem, styles.dropdownItemExtra)}>
                      <div className={bimloaFilterStyles.filterRowHeader}>
                        <label className={bimloaFilterStyles.label}>
                          {t('genericTable.bimloaFilter.chooseDate')}
                        </label>
                        <button
                          className={bimloaFilterStyles.resetBtn}
                          onClick={() => {
                            if (value?.data?.date) {
                              delete value.data.date;
                            }
                            handleOptionClick({ ...value });
                          }}
                          disabled={!value?.data?.date}
                        >
                          {t('genericTable.bimloaFilter.reset')}
                        </button>
                      </div>
                      <div>
                        <DatePicker
                          date={value.data.date}
                          setDate={date => {
                            value.data.date = date;
                            handleOptionClick({ ...value });
                          }}
                        />
                      </div>
                    </li>
                  )}
                {value?.value === option.value &&
                  value?.data?.leaveOpen &&
                  value?.data?.type === 'date_range' && (
                    <li className={classes(styles.dropdownItem, styles.dropdownItemExtra)}>
                      <div className={bimloaFilterStyles.filterRowHeader}>
                        <label className={bimloaFilterStyles.label}>
                          {t('genericTable.bimloaFilter.chooseDateRange')}
                        </label>
                        <button
                          className={bimloaFilterStyles.resetBtn}
                          onClick={() => {
                            if (value?.data?.fromDate) {
                              delete value.data.fromDate;
                            }
                            if (value?.data?.toDate) {
                              delete value.data.toDate;
                            }
                            handleOptionClick({ ...value });
                          }}
                          disabled={!value?.data?.fromDate && !value?.data?.toDate}
                        >
                          {t('genericTable.bimloaFilter.reset')}
                        </button>
                      </div>
                      <div className={bimloaFilterStyles.dateRangeSelector}>
                        <div>
                          <div className={bimloaFilterStyles.datePickerLabel}>
                            {t('genericTable.bimloaFilter.from')}
                          </div>
                          <DatePicker
                            className={bimloaFilterStyles.datePickerLeftBoundary}
                            date={value.data.fromDate}
                            setDate={date => {
                              value.data.fromDate = date;
                              handleOptionClick({ ...value });
                            }}
                            selectsStart={true}
                            startDate={value.data.fromDate}
                            endDate={value.data.toDate}
                            maxDate={value.data.toDate}
                          />
                        </div>
                        <div>
                          <div className={bimloaFilterStyles.datePickerLabel}>
                            {t('genericTable.bimloaFilter.to')}
                          </div>
                          <DatePicker
                            className={bimloaFilterStyles.datePickerRightBoundary}
                            date={value.data.toDate}
                            setDate={date => {
                              value.data.toDate = date;
                              handleOptionClick({ ...value });
                            }}
                            selectsEnd={true}
                            startDate={value.data.fromDate}
                            endDate={value.data.toDate}
                            minDate={value.data.fromDate}
                          />
                        </div>
                      </div>
                    </li>
                  )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
    );
  }
);

DropDown.displayName = 'DropDown';

export default DropDown;
